import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { CheckCircle, Upload, AlertTriangle, FileText, X, Shield, Gavel, Wrench, Smartphone, Users, Clock } from 'lucide-react'
import api from '../../services/api'

const CAT_ICON: Record<string, any> = {
  legal: Gavel, documentation: FileText, safety: Shield,
  role_training: Wrench, system_access: Smartphone, meet_team: Users,
}
const CAT_COLOUR: Record<string, string> = {
  legal: '#DC2626', documentation: '#2D7DD2', safety: '#16A34A',
  role_training: '#D97706', system_access: '#7C3AED', meet_team: '#0891B2',
}
const TIMING_LABEL: Record<string, string> = {
  before_day1: 'Before Day 1', day1: 'Day 1', week1: 'Week 1', month1: 'Month 1', ongoing: 'Ongoing',
}

type UploadState = 'idle' | 'uploading' | 'done' | 'error'

function ItemUploadCard({ item, token }: { item: any; token: string }) {
  const fileRef = useRef<HTMLInputElement>(null)
  const [uploads, setUploads] = useState<any[]>([])
  const [state, setState] = useState<UploadState>('idle')
  const [error, setError] = useState('')
  const [dragOver, setDragOver] = useState(false)
  const [uploaderName, setUploaderName] = useState('')

  const CatIcon = CAT_ICON[item.category] || FileText
  const catColour = CAT_COLOUR[item.category] || '#64748B'
  const done = uploads.length > 0

  const handleFile = async (file: File) => {
    setState('uploading')
    setError('')
    try {
      const form = new FormData()
      form.append('file', file)
      if (uploaderName) form.append('uploader_name', uploaderName)
      const { data } = await api.post(`/onboarding/portal/${token}/items/${item.id}/upload`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      setUploads(prev => [...prev, data])
      setState('done')
    } catch (e: any) {
      setError(e.response?.data?.error || 'Upload failed — please try again')
      setState('error')
    }
  }

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    const file = e.dataTransfer.files[0]
    if (file) handleFile(file)
  }

  return (
    <div style={{
      border: `1.5px solid ${done ? '#16A34A' : item.blocking ? '#DC2626' : '#E2E8F0'}`,
      borderRadius: 12, overflow: 'hidden', background: '#fff',
    }}>
      {/* Header */}
      <div style={{
        padding: '12px 16px',
        background: done ? '#F0FDF4' : item.blocking ? '#FEF2F2' : '#F8FAFC',
        display: 'flex', alignItems: 'flex-start', gap: 10,
      }}>
        <div style={{
          width: 32, height: 32, borderRadius: 8, flexShrink: 0,
          background: done ? '#16A34A' : catColour,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {done
            ? <CheckCircle size={16} color="#fff" />
            : <CatIcon size={16} color="#fff" />
          }
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <p style={{ fontWeight: 700, fontSize: 14, color: '#0F1923', margin: 0 }}>{item.name}</p>
            {item.blocking === 1 && !done && (
              <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: '#DC2626', color: '#fff', fontWeight: 700 }}>REQUIRED</span>
            )}
            {item.sighting_only === 1 && (
              <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: '#FEF3C7', color: '#D97706', fontWeight: 600 }}>sighting only</span>
            )}
            <span style={{ fontSize: 11, color: '#94A3B8' }}>{TIMING_LABEL[item.due_timing] || item.due_timing}</span>
          </div>
          {item.description && <p style={{ fontSize: 12, color: '#64748B', margin: '4px 0 0' }}>{item.description}</p>}
        </div>
      </div>

      {/* Upload area */}
      <div style={{ padding: '14px 16px' }}>
        {/* Uploaded files */}
        {uploads.length > 0 && (
          <div style={{ marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {uploads.map((u, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
                borderRadius: 8, background: '#F0FDF4', border: '1px solid #16A34A',
              }}>
                <CheckCircle size={14} color="#16A34A" style={{ flexShrink: 0 }} />
                <span style={{ fontSize: 12, color: '#166534', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.original_name}</span>
              </div>
            ))}
          </div>
        )}

        {/* Uploader name (first upload only) */}
        {uploads.length === 0 && (
          <div style={{ marginBottom: 10 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#64748B', display: 'block', marginBottom: 4 }}>YOUR NAME (optional)</label>
            <input
              value={uploaderName}
              onChange={e => setUploaderName(e.target.value)}
              placeholder="e.g. Jane Smith"
              style={{ width: '100%', boxSizing: 'border-box', fontSize: 13, padding: '7px 10px', border: '1px solid #E2E8F0', borderRadius: 8 }}
            />
          </div>
        )}

        {/* Dropzone */}
        <div
          onDragOver={e => { e.preventDefault(); setDragOver(true) }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => state !== 'uploading' && fileRef.current?.click()}
          style={{
            border: `2px dashed ${dragOver ? catColour : done ? '#16A34A' : '#CBD5E0'}`,
            borderRadius: 10, padding: '16px 12px', textAlign: 'center', cursor: state === 'uploading' ? 'wait' : 'pointer',
            background: dragOver ? `color-mix(in srgb, ${catColour} 5%, transparent)` : done ? '#F0FDF4' : '#F8FAFC',
            transition: 'all 0.15s',
          }}
        >
          <input ref={fileRef} type="file" accept="image/*,application/pdf" style={{ display: 'none' }}
            onChange={e => { if (e.target.files?.[0]) { handleFile(e.target.files[0]); e.target.value = '' } }} />
          {state === 'uploading' ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', border: `3px solid ${catColour}`, borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite' }} />
              <p style={{ fontSize: 12, color: '#64748B', margin: 0 }}>Uploading…</p>
            </div>
          ) : done ? (
            <p style={{ fontSize: 12, color: '#16A34A', margin: 0, fontWeight: 600 }}>
              ✓ Uploaded — {uploads.length > 1 ? `${uploads.length} files` : '1 file'} · <span style={{ fontWeight: 400 }}>Click to add another</span>
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
              <Upload size={22} color={catColour} />
              <p style={{ fontSize: 13, fontWeight: 600, color: '#0F1923', margin: 0 }}>Drop file here or click to browse</p>
              <p style={{ fontSize: 11, color: '#94A3B8', margin: 0 }}>JPG, PNG, PDF · max 20MB</p>
            </div>
          )}
        </div>

        {error && (
          <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', borderRadius: 8, background: '#FEF2F2', border: '1px solid #DC2626' }}>
            <AlertTriangle size={13} color="#DC2626" style={{ flexShrink: 0 }} />
            <p style={{ fontSize: 12, color: '#DC2626', margin: 0 }}>{error}</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default function UploadPortal() {
  const { token } = useParams<{ token: string }>()
  const [ctx, setCtx] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    api.get(`/onboarding/portal/${token}`)
      .then(r => setCtx(r.data))
      .catch(e => setError(e.response?.data?.error || 'Invalid or expired link'))
      .finally(() => setLoading(false))
  }, [token])

  if (loading) return (
    <div style={{ minHeight: '100vh', background: '#F4F6F9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: 36, height: 36, borderRadius: '50%', border: '3px solid #1B2B4B', borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite' }} />
    </div>
  )

  if (error) return (
    <div style={{ minHeight: '100vh', background: '#F4F6F9', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 32, maxWidth: 400, textAlign: 'center', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
        <AlertTriangle size={40} color="#DC2626" style={{ marginBottom: 12 }} />
        <h2 style={{ fontSize: 18, fontWeight: 700, color: '#0F1923', margin: '0 0 8px' }}>Link Unavailable</h2>
        <p style={{ fontSize: 14, color: '#64748B', margin: 0 }}>{error}</p>
      </div>
    </div>
  )

  const { worker, tenant, items, expires_at, uploaded_by_type } = ctx
  const expiresDate = expires_at ? new Date(expires_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' }) : ''
  const blockingItems = items.filter((i: any) => i.blocking === 1)
  const otherItems = items.filter((i: any) => i.blocking !== 1)

  return (
    <div style={{ minHeight: '100vh', background: '#F4F6F9' }}>
      {/* Header */}
      <div style={{ background: '#1B2B4B', padding: '20px 24px' }}>
        <div style={{ maxWidth: 680, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 16 }}>
          {tenant?.logo_url && (
            <img src={tenant.logo_url} alt="logo" style={{ height: 36, borderRadius: 6, background: '#fff', padding: 4 }} />
          )}
          <div>
            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', margin: '0 0 2px', fontWeight: 600, letterSpacing: 0.5 }}>
              {tenant?.name?.toUpperCase() || 'HSEQ INTELLIGENCE'}
            </p>
            <h1 style={{ fontSize: 16, fontWeight: 700, color: '#fff', margin: 0 }}>Document Upload Portal</h1>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 680, margin: '0 auto', padding: '24px 16px 48px' }}>
        {/* Worker / context card */}
        <div style={{ background: '#fff', borderRadius: 14, padding: '20px 24px', marginBottom: 24, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
            <div style={{
              width: 48, height: 48, borderRadius: '50%', background: '#1B2B4B',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 18, fontWeight: 700, color: '#fff', flexShrink: 0,
            }}>
              {worker.first_name?.[0]}{worker.last_name?.[0]}
            </div>
            <div style={{ flex: 1 }}>
              <h2 style={{ fontSize: 18, fontWeight: 700, color: '#0F1923', margin: '0 0 4px' }}>
                {worker.first_name} {worker.last_name}
              </h2>
              {worker.job_title && <p style={{ fontSize: 13, color: '#4A5568', margin: 0 }}>{worker.job_title}{worker.department ? ` · ${worker.department}` : ''}</p>}
            </div>
          </div>

          <div style={{ marginTop: 16, padding: '12px 14px', borderRadius: 10, background: '#F8FAFC', border: '1px solid #E2E8F0' }}>
            <p style={{ fontSize: 13, color: '#4A5568', margin: '0 0 4px' }}>
              Please upload the required documents for <strong>{worker.first_name}'s</strong> onboarding below.
              {uploaded_by_type === 'worker' ? ' These can be photos of your licences or certificates.' : ''}
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
              <Clock size={12} color="#94A3B8" />
              <p style={{ fontSize: 11, color: '#94A3B8', margin: 0 }}>Link expires {expiresDate}</p>
            </div>
          </div>
        </div>

        {items.length === 0 ? (
          <div style={{ background: '#fff', borderRadius: 14, padding: 32, textAlign: 'center', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
            <CheckCircle size={40} color="#16A34A" style={{ marginBottom: 12 }} />
            <h3 style={{ fontSize: 16, fontWeight: 700, color: '#0F1923', margin: '0 0 6px' }}>All done!</h3>
            <p style={{ fontSize: 13, color: '#64748B', margin: 0 }}>No documents are currently required from you.</p>
          </div>
        ) : (
          <>
            {blockingItems.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                  <AlertTriangle size={14} color="#DC2626" />
                  <p style={{ fontSize: 12, fontWeight: 700, color: '#DC2626', margin: 0, textTransform: 'uppercase', letterSpacing: 0.6 }}>
                    Required before work can begin
                  </p>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {blockingItems.map((item: any) => (
                    <ItemUploadCard key={item.id} item={item} token={token!} />
                  ))}
                </div>
              </div>
            )}

            {otherItems.length > 0 && (
              <div>
                {blockingItems.length > 0 && (
                  <p style={{ fontSize: 12, fontWeight: 700, color: '#64748B', margin: '0 0 12px', textTransform: 'uppercase', letterSpacing: 0.6 }}>
                    Additional documents
                  </p>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {otherItems.map((item: any) => (
                    <ItemUploadCard key={item.id} item={item} token={token!} />
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        <div style={{ marginTop: 32, textAlign: 'center' }}>
          <p style={{ fontSize: 11, color: '#94A3B8' }}>
            Powered by HSEQ Intelligence · Documents are stored securely in Australia
          </p>
        </div>
      </div>
    </div>
  )
}
